// INFINITE SCROLLER
import { Box, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui-compat"
import { ActivityIndicator } from "components/activity-indicator"
import { HorizontalScroll, ScrollerProps } from "components/common/HorizontalScroll"
import { getChildrenByType } from "lib/react-utils"
import React, { useCallback, useRef } from "react"

const useStyles = makeStyles()(
  (theme) => ({
    loadingIndicatorContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100%",
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },
  })
)

export interface InfiniteScrollerProps extends ScrollerProps {
  hasMore: boolean
  loadMore: () => void
  isLoading: boolean
}

const InfiniteScroller = (props: InfiniteScrollerProps) => {
  const { children, isLoading, hasMore, loadMore, scrollOffset } = props
  const { classes } = useStyles()
  const heading = getChildrenByType(
    children,
    HorizontalScroll.Heading,
  ) as unknown as React.ReactElement<typeof Typography>
  const content = getChildrenByType(children, HorizontalScroll.Content)

  const loadMoreObserverRef = useRef<IntersectionObserver | null>(null)

  const createLoadMoreObserver = useCallback(
    (node: HTMLElement | null) => {
      if (isLoading) return
      if (loadMoreObserverRef.current) loadMoreObserverRef.current.disconnect()
      loadMoreObserverRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) loadMore()
      })
      if (node) loadMoreObserverRef.current.observe(node)
    },
    [isLoading, hasMore],
  )

  const observerElement = !isLoading && hasMore && (
    <Box aria-hidden="true" {...{ ref: createLoadMoreObserver }} />
  )

  return (
    <HorizontalScroll.Scroller scrollOffset={scrollOffset}>
      <HorizontalScroll.Heading>{heading}</HorizontalScroll.Heading>
      <HorizontalScroll.Content>
        {content}
        {observerElement}

        {(isLoading || hasMore) && (
          <Box className={classes.loadingIndicatorContainer}>
            <ActivityIndicator indicatorSize={25} />
          </Box>
        )}
      </HorizontalScroll.Content>
    </HorizontalScroll.Scroller>
  )
}

export const InfiniteHorizontalScroll = {
  Scroller: InfiniteScroller,
  Heading: HorizontalScroll.Heading,
  Content: HorizontalScroll.Content,
}
